import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Newspaper, CalendarDays, GraduationCap, Inbox } from "lucide-react";

export default function Dashboard() {
    const { admin } = useAuth();
    const [stats, setStats] = useState({ blog: 0, events: 0, trainings: 0, unread: 0 });
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        const calls = [
            api.get("/admin/blog"),
            api.get("/admin/events"),
            api.get("/admin/trainings"),
            api.get("/admin/audit"),
        ];
        if (admin?.is_owner) calls.push(api.get("/admin/messages"));
        Promise.all(calls).then(([b, e, t, a, m]) => {
            setStats({
                blog: b.data.length,
                events: e.data.length,
                trainings: t.data.length,
                unread: m ? m.data.filter((x) => !x.read).length : 0,
            });
            setRecent(a.data.slice(0, 8));
        });
    }, [admin]);

    const cards = [
        { label: "Blog posts", value: stats.blog, icon: Newspaper, to: "/admin/blog" },
        { label: "Events", value: stats.events, icon: CalendarDays, to: "/admin/events" },
        { label: "Trainings", value: stats.trainings, icon: GraduationCap, to: "/admin/trainings" },
        ...(admin?.is_owner
            ? [{ label: "Unread messages", value: stats.unread, icon: Inbox, to: "/admin/messages" }]
            : []),
    ];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-serif text-3xl text-blue-50">
                Hello, {admin?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="mt-1 text-sm text-slate-300/55">
                Here's what's happening in the warm room.
            </p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {cards.map((c) => (
                    <Link
                        key={c.label}
                        to={c.to}
                        className="rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6 transition-colors hover:border-blue-400/50"
                    >
                        <c.icon className="h-6 w-6 text-blue-400" />
                        <p className="mt-4 font-serif text-4xl text-slate-300">
                            {c.value}
                        </p>
                        <p className="mt-1 text-sm text-slate-300/55">{c.label}</p>
                    </Link>
                ))}
            </div>

            <div className="mt-10">
                <h2 className="font-serif text-xl text-slate-300">Recent activity</h2>
                <div className="mt-4 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-2">
                    {recent.length === 0 && (
                        <p className="px-4 py-6 text-sm text-slate-300/45">
                            No activity yet.
                        </p>
                    )}
                    {recent.map((r) => (
                        <div
                            key={r.id}
                            className="flex items-center justify-between border-b border-blue-500/10 px-4 py-3 text-sm last:border-b-0"
                        >
                            <span className="text-slate-300/80">
                                <span className="text-sky-400">{r.actor_email}</span>{" "}
                                {r.action}
                                {r.entity_title ? (
                                    <span className="text-slate-300/60"> — {r.entity_title}</span>
                                ) : null}
                            </span>
                            <span className="text-xs text-slate-300/40">
                                {new Date(r.timestamp).toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
